/********************************************************************************
 * Copyright (c) 2020 TypeFox and others
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License v. 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 ********************************************************************************/

import { FunctionComponent, ReactNode, Suspense, lazy, useContext } from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { Theme } from '@mui/material/styles/createTheme';
import { SxProps } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { Helmet, HelmetTags } from 'react-helmet-async';
import { Link as RouteLink, Route, useParams } from 'react-router';
import { PageSettings, Extension, NamespaceDetails, OpenVsxMark } from 'openvsx-webui';
import { ExtensionListRoutes } from 'openvsx-webui/lib/pages/extension-list/extension-list-routes';
import { DefaultMenuContent, MobileMenuContent } from './menu-content';
import OpenVSXLogo from './openvsx-registry-logo';
import { Document } from './document';
import About from './about';
import Adopters from './adopters';
import ErrorHelp from './error-help';
import ErrorPage from './error-page';
import Members from './members';
import { Home } from './home';
import { AnalyticsMenuItem } from './analytics/analytics-menu-item';
import { AnalyticsRoutes } from './analytics/analytics-routes';
import { MainContext } from 'openvsx-webui/lib/context';
import { useHasUserCustomers } from './rate-limiting/user/use-user-customers';
import BuildIcon from '@mui/icons-material/Build';
import ComputerIcon from '@mui/icons-material/Computer';
import BarChartIcon from '@mui/icons-material/BarChart';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import GitHubIcon from '@mui/icons-material/GitHub';
import InsightsIcon from '@mui/icons-material/Insights';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PeopleIcon from '@mui/icons-material/People';
import SpeedIcon from '@mui/icons-material/Speed';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import StarIcon from '@mui/icons-material/Star';
import XIcon from '@mui/icons-material/X';

// Deferred: the dashboard pulls in @mui/x-charts, which would otherwise land on every
// visitor's critical path. The library's AppLayout renders additionalRoutes inside a Suspense.
const AnalyticsPage = lazy(() => import('./analytics/analytics-page').then((m) => ({ default: m.AnalyticsPage })));

// Admin-only, and the admin dashboard renders contributed pages inside a Suspense too.
const AnalyticsAgentsPage = lazy(() =>
  import('./analytics/admin/agents-page').then((m) => ({ default: m.AnalyticsAgentsPage }))
);
// The user settings page renders inside AppLayout's Suspense, and this tab charts usage.
const UserCustomersPage = lazy(() =>
  import('./rate-limiting/user/user-customers').then((m) => ({ default: m.UserCustomers }))
);
const TiersPage = lazy(() => import('./rate-limiting/admin/tiers/tiers').then((m) => ({ default: m.Tiers })));
const CustomersPage = lazy(() =>
  import('./rate-limiting/admin/customers/customers-page').then((m) => ({ default: m.CustomersPage }))
);
const UsageStatsPage = lazy(() =>
  import('./rate-limiting/admin/usage-stats/usage-stats-page').then((m) => ({ default: m.UsageStatsPage }))
);
const FastlyPage = lazy(() => import('./fastly/fastly-page').then((m) => ({ default: m.FastlyPage })));

const WIKI_URL = 'https://github.com/EclipseFdn/open-vsx.org/wiki';
const REPO_URL = 'https://github.com/eclipse-openvsx/openvsx';

//---------- HEAD TAGS
const HeadTags: FunctionComponent<{
  title?: string;
  description?: string;
  keywords?: string;
  url?: string;
  imageUrl?: string;
}> = (props) => {
  const handleChangeClientState = (newState: any, addedTags: HelmetTags, removedTags: HelmetTags): void => {
    if (addedTags.metaTags) {
      addedTags.metaTags.forEach((value: HTMLMetaElement) => {
        if (!value.content) {
          value.remove();
        }
      });
    }
  };

  const twitterCard = props.imageUrl ? 'summary_large_image' : 'summary';
  return (
    <Helmet onChangeClientState={handleChangeClientState}>
      <title>{props.title}</title>

      {/* SEO Meta Tags */}
      <meta name='description' content={props.description} />
      <meta name='keywords' content={props.keywords} />
      <meta property='og:url' content={props.url} />
      <meta property='og:type' content='website' />
      <meta property='og:title' content={props.title} />
      <meta property='og:description' content={props.description} />
      <meta property='og:image' content={props.imageUrl} />

      {/* Google Meta Tags */}
      <meta itemProp='name' content={props.title} />
      <meta itemProp='description' content={props.description} />
      <meta itemProp='image' content={props.imageUrl} />

      {/* Twitter Meta Tags */}
      <meta name='twitter:card' content={twitterCard} />
      <meta name='twitter:title' content={props.title} />
      <meta name='twitter:description' content={props.description} />
      <meta name='twitter:image' content={props.imageUrl} />
    </Helmet>
  );
};

const MainHeadTags: FunctionComponent<{ pageSettings: PageSettings }> = (props) => {
  const title = props.pageSettings.pageTitle;
  const description =
    'Open VSX is an Eclipse open-source project and alternative to the Visual Studio Marketplace. It is deployed by the Eclipse Foundation at open-vsx.org.';
  const keywords =
    'eclipse,ide,open source,development environment,development,vs code,visual studio code,extension,plugin,plug-in,registry,theia';
  const url = `${location.protocol}//${location.host}`;
  const imageUrl = url + '/openvsx-preview.png';

  return <HeadTags title={title} description={description} keywords={keywords} url={url} imageUrl={imageUrl} />;
};

const ExtensionHeadTags: FunctionComponent<{ extension?: Extension; pageSettings: PageSettings }> = (props) => {
  const { name, namespace } = useParams();
  let title = ` – ${props.pageSettings.pageTitle}`;
  let url = `${location.protocol}//${location.host}/extension/`;
  let description: string | undefined;
  let keywords: string | undefined;
  if (props.extension) {
    title = (props.extension.displayName ?? props.extension.name) + title;
    url += `${props.extension.namespace}/${props.extension.name}`;
    description = props.extension.description;
    // extension description can be up to 2048 characters, truncate it.
    if (description && description.length > 255) {
      let lastWordIndex = description.indexOf(' ', 255);
      lastWordIndex = lastWordIndex !== -1 ? lastWordIndex - 1 : 255;
      description = description.substring(0, lastWordIndex);
    }
    if (props.extension.tags) {
      keywords = props.extension.tags.filter((t) => !t.startsWith('__')).join();
    }
  } else {
    title = name + title;
    url += `${namespace}/${name}`;
  }

  return <HeadTags title={title} url={url} description={description} keywords={keywords} />;
};

const NamespaceHeadTags: FunctionComponent<{ namespaceDetails?: NamespaceDetails; pageSettings: PageSettings }> = (
  props
) => {
  const { name } = useParams();
  const namespaceName = props.namespaceDetails?.displayName ?? props.namespaceDetails?.name ?? name;
  const title = `${namespaceName} – ${props.pageSettings.pageTitle}`;
  const url = `${location.protocol}//${location.host}/namespace/${namespaceName}`;
  const description = props.namespaceDetails?.description;
  return <HeadTags title={title} url={url} description={description} />;
};

export default function createPageSettings(theme: Theme, prefersDarkMode: boolean, serverUrl = ''): PageSettings {
  //---------- SERVER VERSION
  const ServerVersion: FunctionComponent = () => {
    const { version } = useContext(MainContext);
    if (!version) {
      return <div>Loading version...</div>;
    }
    return (
      <Typography variant='body2' sx={{ alignSelf: 'flex-start', fontSize: '0.8rem' }}>
        {version.version}
      </Typography>
    );
  };

  //---------- MAIN LOGO / TOOLBAR
  const toolbarContent: FunctionComponent = () => {
    const { user } = useContext(MainContext);

    return (
      <>
        <RouteLink
          to={ExtensionListRoutes.MAIN}
          aria-label={`Home - Open VSX Registry`}
          // A bare anchor leaks the browser's link color into the wordmark; color:inherit
          // lets the logo follow the navbar's content color, which is tinted on extension bands.
          style={{ display: 'flex', color: 'inherit' }}>
          <OpenVSXLogo width='auto' height='2.5rem' />
        </RouteLink>
        {user?.role === 'admin' && (
          <Suspense>
            <ServerVersion />
          </Suspense>
        )}
      </>
    );
  };

  //---------- DOWNLOAD TERMS
  const downloadTerms: FunctionComponent = () => (
    <Box mt={1}>
      <Typography variant='body2'>
        By clicking download, you accept this website&apos;s&nbsp;
        <Link color='secondary' underline='hover' href='https://open-vsx.org/terms-of-use'>
          Terms of Use
        </Link>
        .
      </Typography>
    </Box>
  );

  //---------- ADDITIONAL PAGES
  const additionalRoutes: ReactNode = (
    <>
      <Route path='/about' element={<About />} />
      <Route path='/terms-of-use' element={<Document url='/documents/terms-of-use.md' />} />
      <Route path='/publisher-agreement-v1.1' element={<Document url='/documents/publisher-agreement-v1.1.md' />} />
      <Route path='/members' element={<Members />} />
      <Route path='/adopters' element={<Adopters />} />
      <Route path='/error' element={<ErrorPage />} />
      <Route path={AnalyticsRoutes.MAIN} element={<AnalyticsPage />} />
    </>
  );

  //---------- ADMIN DASHBOARD PAGES
  const rateLimiting = { name: 'Rate Limiting', icon: <SpeedIcon /> };
  const adminPages: PageSettings['elements']['adminPages'] = [
    {
      path: 'tiers',
      name: 'Tiers',
      icon: <StarIcon />,
      description: 'Manage rate-limit tiers',
      category: rateLimiting,
      permission: 'manage_rate_limits',
      element: <TiersPage />
    },
    {
      path: 'customers',
      name: 'Customers',
      icon: <PeopleIcon />,
      description: 'Manage rate-limit customers',
      category: rateLimiting,
      permission: 'manage_rate_limits',
      element: <CustomersPage />
    },
    {
      path: 'usage',
      name: 'Usage Stats',
      icon: <BarChartIcon />,
      description: 'Show usage stats for customers',
      category: rateLimiting,
      permission: 'manage_rate_limits',
      element: <UsageStatsPage />
    },
    {
      path: 'analytics/agents',
      name: 'Agents',
      icon: <ComputerIcon />,
      description: 'Link rate-limit customers to analytics user agents',
      category: { name: 'Analytics', icon: <InsightsIcon /> },
      element: <AnalyticsAgentsPage />
    },
    {
      path: 'fastly',
      name: 'CDN',
      icon: <DeleteSweepIcon />,
      description: 'Purge paths from the Fastly cache',
      // the same built-in group as the Caches page; the icon only matters if that group ever disappears
      category: { name: 'Maintenance', icon: <BuildIcon /> },
      permission: 'manage_caches',
      element: <FastlyPage />
    }
  ];

  //---------- USER SETTINGS TABS
  const userSettingsTabs: PageSettings['elements']['userSettingsTabs'] = [
    {
      value: 'customers',
      label: 'Rate Limiting',
      icon: <SpeedOutlinedIcon />,
      element: <UserCustomersPage />,
      useIsApplicable: useHasUserCustomers
    }
  ];

  //---------- ADMIN SETTINGS SWITCHES
  // Joins the built-in Analytics section, which is shown only where the server has analytics configured.
  const adminSettingsSections: PageSettings['elements']['adminSettingsSections'] = [
    {
      id: 'analytics',
      flags: [
        {
          key: 'eclipsefdnRegistryAnalyticsVisible',
          title: 'Registry Analytics dashboard for customers',
          description:
            'Admins always have access, so they can check backfilled data first. Switching this on opens the dashboard and its endpoints to customer members.'
        }
      ]
    }
  ];

  //---------- REPORT ABUSE LINK
  const reportAbuse: FunctionComponent<{ extension: Extension; sx: SxProps<Theme> }> = ({ extension, sx }) => {
    const reportAbuseText = encodeURIComponent('<Please describe the issue>');
    const extensionURL = encodeURIComponent(
      `${location.protocol}//${location.hostname}/extension/${extension.namespace}/${extension.name}`
    );
    return (
      <Link
        href={`mailto:security@open-vsx.org?subject=Report%20Abuse%20-%20${extension.namespace}.${extension.name}&Body=${reportAbuseText}%0A%0A${extensionURL}`}
        variant='body2'
        color='secondary'
        underline='hover'
        sx={sx}>
        Report Abuse
      </Link>
    );
  };

  //---------- CLAIM NAMESPACE LINK
  // extension is optional: the namespace-not-verified notice renders this without one, and in
  // that case the notice has already established the namespace is unverified.
  const claimNamespace: FunctionComponent<{
    namespace: string;
    extension?: Extension;
    sx?: SxProps<Theme>;
  }> = ({ sx, namespace, extension }) => {
    if (extension?.verified) {
      return null;
    }
    const title = `Claiming namespace \`${namespace}\``;

    return (
      <Link
        href={`https://github.com/EclipseFdn/open-vsx.org/issues/new?template=claim-namespace-ownership.yml&namespace=${encodeURIComponent(namespace)}&title=${encodeURIComponent(title)}`}
        target='_blank'
        variant='body2'
        color='secondary'
        underline='hover'
        sx={sx}>
        Claim Ownership
      </Link>
    );
  };

  //---------- FOOTER
  const footer: PageSettings['elements']['footer'] = {
    brand: {
      logo: <OpenVsxMark />,
      name: 'Open VSX Registry',
      description: 'An open source, vendor-neutral registry for VS Code–compatible extensions.'
    },
    columns: [
      {
        heading: 'Resources',
        links: [
          { label: 'Documentation', href: WIKI_URL, external: true },
          { label: 'Status', href: 'https://status.open-vsx.org/', external: true },
          { label: 'Commercial Usage', href: 'https://managed.open-vsx.org/', external: true },
          { label: 'Report a Vulnerability', href: 'https://researcher-recognition.open-vsx.org', external: true },
          { label: 'Sponsor', href: 'https://www.eclipse.org/donate/openvsx/', external: true }
        ]
      },
      {
        heading: 'Community',
        links: [
          { label: 'About This Service', href: '/about' },
          { label: 'Members', href: '/members' },
          { label: 'Adopters', href: '/adopters' }
        ]
      },
      {
        heading: 'Legal',
        links: [
          { label: 'OSS Access', href: 'https://managed.open-vsx.org/contact', external: true },
          { label: 'Privacy Policy', href: 'https://www.eclipse.org/legal/privacy/', external: true },
          { label: 'Terms of Use', href: '/terms-of-use' },
          // a full page load, so the server's redirect to the policy answers; a route link would hit the app's 404
          { label: 'Security Policy', href: 'https://open-vsx.org/security', external: true },
          { label: 'Compliance', href: 'https://www.eclipse.org/legal/compliance/', external: true },
          { label: 'Legal Resources', href: 'http://www.eclipse.org/legal/', external: true }
        ]
      }
    ],
    social: [
      { title: 'Open VSX on GitHub', href: REPO_URL, icon: <GitHubIcon sx={{ fontSize: '1rem' }} /> },
      {
        title: 'Open VSX on LinkedIn',
        href: 'https://www.linkedin.com/company/open-vsx/',
        icon: <LinkedInIcon sx={{ fontSize: '1rem' }} />
      },
      { title: 'Open VSX on X (Twitter)', href: 'https://x.com/openvsx', icon: <XIcon sx={{ fontSize: '1rem' }} /> }
    ],
    copyright: (
      <>
        Copyright &copy;{' '}
        <Link href='https://www.eclipse.org' color='inherit' underline='hover'>
          Eclipse Foundation, AISBL.
        </Link>{' '}
        All Rights Reserved.
      </>
    ),
    extra: (
      <Box
        component='span'
        className='toolbar-manage-cookies'
        sx={{
          cursor: 'pointer',
          fontSize: '0.75rem',
          color: 'text.disabled',
          '&:hover': { color: 'secondary.light' }
        }}>
        Manage Cookies
      </Box>
    )
  };

  return {
    pageTitle: 'Open VSX Registry',
    themeType: prefersDarkMode ? 'dark' : 'light',
    publisherAgreement: {
      name: 'Eclipse Foundation Open VSX',
      email: 'openvsx@eclipse-foundation.org'
    },
    elements: {
      defaultMenuContent: DefaultMenuContent,
      mobileMenuContent: MobileMenuContent,
      userMenuContent: AnalyticsMenuItem,
      toolbarContent,
      footer,
      home: Home,
      downloadTerms,
      errorHelp: ErrorHelp,
      additionalRoutes,
      adminPages,
      userSettingsTabs,
      adminSettingsSections,
      reportAbuse,
      claimNamespace,
      mainHeadTags: MainHeadTags,
      extensionHeadTags: ExtensionHeadTags,
      namespaceHeadTags: NamespaceHeadTags
    },
    urls: {
      extensionDefaultIcon: '/default-icon.png',
      namespaceAccessInfo: 'https://github.com/eclipse-openvsx/openvsx/wiki/Namespace-Access',
      // served by the server from ovsx.publisher-agreement.text, so the text it shows is the one that is signed
      publisherAgreement: `${serverUrl}/api/publisher-agreement`,
      trustedPublishing: 'https://github.com/eclipse-openvsx/openvsx/wiki/Trusted-Publishing'
    }
  };
}
